import { FeatureFlagConfig, CORE_FEATURES } from './types';

// Get current timestamp
const now = new Date().toISOString();

// Development environment - All features enabled for testing
export const DEVELOPMENT_FLAGS: FeatureFlagConfig = {
  [CORE_FEATURES.ENHANCED_DASHBOARD]: {
    enabled: true,
    description: 'Enhanced dashboard with real-time analytics, interactive charts, and quick action panels',
    rolloutPercentage: 100,
    createdAt: now,
    updatedAt: now,
    metadata: {
      priority: 'high',
      phase: 1,
      components: ['charts', 'analytics', 'quick-actions', 'real-time-data']
    }
  },
  [CORE_FEATURES.ADVANCED_DRIVER_MANAGEMENT]: {
    enabled: true,
    description: 'Comprehensive driver management with performance analytics, photo profiles, and assignment optimization',
    rolloutPercentage: 100,
    createdAt: now,
    updatedAt: now,
    metadata: {
      priority: 'high',
      phase: 1,
      components: ['driver-profiles', 'performance-analytics', 'assignment-history', 'document-management']
    }
  },
  [CORE_FEATURES.COMPREHENSIVE_VEHICLE_TRACKING]: {
    enabled: true,
    description: 'Advanced vehicle management with real-time tracking, maintenance scheduling, and utilization analytics',
    rolloutPercentage: 100,
    createdAt: now,
    updatedAt: now,
    metadata: {
      priority: 'medium',
      phase: 2,
      components: ['real-time-tracking', 'maintenance-scheduler', 'cost-analytics', 'utilization-metrics']
    }
  },
  [CORE_FEATURES.DETAILED_ASSIGNMENT_HISTORY]: {
    enabled: true,
    description: 'Enhanced assignment history with timeline view, advanced filtering, and performance metrics',
    rolloutPercentage: 100,
    createdAt: now,
    updatedAt: now,
    metadata: {
      priority: 'medium',
      phase: 2,
      components: ['timeline-view', 'advanced-filters', 'export-functionality', 'performance-metrics']
    }
  },
  [CORE_FEATURES.REAL_TIME_ANALYTICS]: {
    enabled: true,
    description: 'Real-time data synchronization and live analytics across all features',
    rolloutPercentage: 100,
    createdAt: now,
    updatedAt: now,
    metadata: {
      priority: 'high',
      phase: 1,
      components: ['websocket-connections', 'live-charts', 'real-time-notifications']
    }
  },
  [CORE_FEATURES.MOBILE_OPTIMIZATION]: {
    enabled: true,
    description: 'Mobile-first responsive design and touch-optimized interfaces',
    rolloutPercentage: 100,
    createdAt: now,
    updatedAt: now,
    metadata: {
      priority: 'high',
      phase: 1,
      components: ['responsive-design', 'touch-optimization', 'mobile-navigation']
    }
  },
};

// Staging environment - Testing rollout strategy
export const STAGING_FLAGS: FeatureFlagConfig = {
  [CORE_FEATURES.ENHANCED_DASHBOARD]: {
    ...DEVELOPMENT_FLAGS[CORE_FEATURES.ENHANCED_DASHBOARD],
    enabled: true,
    rolloutPercentage: 100,
    userTargeting: {
      userTypes: ['admin', 'manager', 'owner'],
      rolloutPercentage: 100,
    },
  },
  [CORE_FEATURES.ADVANCED_DRIVER_MANAGEMENT]: {
    ...DEVELOPMENT_FLAGS[CORE_FEATURES.ADVANCED_DRIVER_MANAGEMENT],
    enabled: true,
    rolloutPercentage: 80, // Gradual rollout testing
    userTargeting: {
      userTypes: ['admin', 'manager'],
      rolloutPercentage: 100,
    },
  },
  [CORE_FEATURES.COMPREHENSIVE_VEHICLE_TRACKING]: {
    ...DEVELOPMENT_FLAGS[CORE_FEATURES.COMPREHENSIVE_VEHICLE_TRACKING],
    enabled: true,
    rolloutPercentage: 60, // Limited rollout for testing
    userTargeting: {
      userTypes: ['admin'],
      rolloutPercentage: 100,
    },
  },
  [CORE_FEATURES.DETAILED_ASSIGNMENT_HISTORY]: {
    ...DEVELOPMENT_FLAGS[CORE_FEATURES.DETAILED_ASSIGNMENT_HISTORY],
    enabled: true,
    rolloutPercentage: 100,
  },
  [CORE_FEATURES.REAL_TIME_ANALYTICS]: {
    ...DEVELOPMENT_FLAGS[CORE_FEATURES.REAL_TIME_ANALYTICS],
    enabled: false, // Testing performance impact
    rolloutPercentage: 50,
    userTargeting: {
      userTypes: ['admin'],
      rolloutPercentage: 100,
    },
  },
  [CORE_FEATURES.MOBILE_OPTIMIZATION]: {
    ...DEVELOPMENT_FLAGS[CORE_FEATURES.MOBILE_OPTIMIZATION],
    enabled: true,
    rolloutPercentage: 100,
  },
};

// Production environment - Conservative rollout (Phase 1 focus)
export const PRODUCTION_FLAGS: FeatureFlagConfig = {
  [CORE_FEATURES.ENHANCED_DASHBOARD]: {
    ...DEVELOPMENT_FLAGS[CORE_FEATURES.ENHANCED_DASHBOARD],
    enabled: true, // Phase 1 - Ready for production
    rolloutPercentage: 100,
    userTargeting: {
      userTypes: ['admin', 'manager', 'owner'],
      rolloutPercentage: 100,
    },
  },
  [CORE_FEATURES.ADVANCED_DRIVER_MANAGEMENT]: {
    ...DEVELOPMENT_FLAGS[CORE_FEATURES.ADVANCED_DRIVER_MANAGEMENT],
    enabled: false, // Phase 2 - Gradual rollout starting
    rolloutPercentage: 25,
    userTargeting: {
      userTypes: ['admin'],
      rolloutPercentage: 100,
    },
  },
  [CORE_FEATURES.COMPREHENSIVE_VEHICLE_TRACKING]: {
    ...DEVELOPMENT_FLAGS[CORE_FEATURES.COMPREHENSIVE_VEHICLE_TRACKING],
    enabled: false, // Phase 3 - Not ready for production
    rolloutPercentage: 0,
    userTargeting: {
      userTypes: ['admin'],
      rolloutPercentage: 25,
    },
  },
  [CORE_FEATURES.DETAILED_ASSIGNMENT_HISTORY]: {
    ...DEVELOPMENT_FLAGS[CORE_FEATURES.DETAILED_ASSIGNMENT_HISTORY],
    enabled: true, // Phase 1 - Ready for production
    rolloutPercentage: 100,
  },
  [CORE_FEATURES.REAL_TIME_ANALYTICS]: {
    ...DEVELOPMENT_FLAGS[CORE_FEATURES.REAL_TIME_ANALYTICS],
    enabled: false, // Coming soon - performance testing needed
    rolloutPercentage: 0,
    userTargeting: {
      userTypes: ['admin'],
      rolloutPercentage: 10,
    },
  },
  [CORE_FEATURES.MOBILE_OPTIMIZATION]: {
    ...DEVELOPMENT_FLAGS[CORE_FEATURES.MOBILE_OPTIMIZATION],
    enabled: true, // Always enabled for mobile experience
    rolloutPercentage: 100,
  },
};

// Get environment-specific flags
export const getEnvironmentFlags = (environment: 'development' | 'staging' | 'production'): FeatureFlagConfig => {
  switch (environment) {
    case 'development':
      return DEVELOPMENT_FLAGS;
    case 'staging':
      return STAGING_FLAGS;
    case 'production':
      return PRODUCTION_FLAGS;
    default:
      return DEVELOPMENT_FLAGS;
  }
};

// Default feature flag configuration
export const DEFAULT_FEATURE_FLAGS = DEVELOPMENT_FLAGS;
